<mat-card class="section z2-elevation mat-elevation-z2">
  <mat-card-content>
    <!-- Section heading area: render wrapper always so actions are visible even when no heading -->
    <div class="section-heading-wrapper" [class.editing]="editingHeading">
      @if (editMode) {
        <button
          class="section-delete-btn"
          mat-mini-fab
          color="warn"
          (click)="removeSection()"
          matTooltip="Delete section"
          aria-label="Delete section"
        >
          <mat-icon>delete</mat-icon>
        </button>
        <button class="section-drag-btn" mat-mini-fab cdkDragHandle matTooltip="Drag to reorder" aria-label="Drag section">
          <mat-icon>drag_indicator</mat-icon>
        </button>
        @if (isNestedSection) {
          <button
            class="section-move-top-btn"
            mat-mini-fab
            color="accent"
            (click)="moveSectionToTopLevel()"
            matTooltip="Move section to top level"
            aria-label="Move section to top level"
          >
            <mat-icon>vertical_align_top</mat-icon>
          </button>
        }
      }
      @if (!editingHeading && heading) {
        <h3
          id="section-{{ documentNode?.id }}"
          class="section-heading"
          (click)="startEditHeading()"
          [class.clickable]="editMode"
          matTooltip="Click to edit heading"
          *ngIf="editMode"
          tabindex="0"
        >
          <span class="chapter-number" *ngIf="chapterNumber">{{ chapterNumber }}</span>
          {{ heading }}
        </h3>
        <h3 id="section-{{ documentNode?.id }}" class="section-heading" *ngIf="!editMode">
          <span class="chapter-number" *ngIf="chapterNumber">{{ chapterNumber }}</span>
          {{ heading }}
        </h3>
      } @else if (editingHeading) {
        <div class="heading-edit-bar">
          <input
            matInput
            class="heading-edit-input"
            [formControl]="headingEditControl"
            (keydown)="onHeadingInputKey($event)"
            (blur)="onHeadingBlur()"
            placeholder="Section heading"
          />
          <button
            mat-icon-button
            color="primary"
            (mousedown)="$event.preventDefault()"
            (click)="saveEditHeading()"
            aria-label="Save heading"
            matTooltip="Save"
          >
            <mat-icon>check</mat-icon>
          </button>
          <button
            mat-icon-button
            (mousedown)="$event.preventDefault()"
            (click)="cancelEditHeading()"
            aria-label="Cancel edit"
            matTooltip="Cancel"
          >
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }
    </div>

    @if (editMode$ | async; as editMode) {
      <!-- Add button to insert a new node at the start of the section -->
      <div class="section-add-top" *ngIf="editMode">
        <button
          mat-mini-fab
          color="primary"
          class="add-btn"
          (click)="addButtonClicked('start')"
          matTooltip="Add node at start"
          aria-label="Add node at start"
        >
          <mat-icon>add</mat-icon>
        </button>
        <span class="add-label">Add</span>
        @if (showingAddAt === 'start') {
          <span class="add-controls">
            <select [(ngModel)]="newNodeTypeSelection['start']">
              @for (type of nodeTypes; track type) {
                <option [ngValue]="type">{{ nodeTypeLabel[type] }}</option>
              }
            </select>
            <button
              mat-mini-fab
              color="primary"
              class="confirm-btn"
              (click)="confirmAdd('start')"
              matTooltip="Confirm add"
              aria-label="Confirm add"
            >
              <mat-icon>check</mat-icon>
            </button>
            <button mat-mini-fab class="cancel-btn" (click)="cancelAdd()" matTooltip="Cancel" aria-label="Cancel add">
              <mat-icon>close</mat-icon>
            </button>
          </span>
        }
      </div>
    }

    <div class="children-drop-wrapper" cdkDropList [cdkDropListDisabled]="!editMode" (cdkDropListDropped)="onChildSectionsDrop($event)">
      @for (node of nodes; track node.id; let i = $index) {
        <div class="section-node-wrapper" [class.draggable-child-section]="isSection(node)" *ngIf="node" [attr.data-node-id]="node?.id">
          <!-- Reviewer mode: add comment action -->
          @if (reviewerMode) {
            <button
              class="reviewer-comment-btn"
              mat-mini-fab
              color="accent"
              (click)="addComment(node)"
              aria-label="Add comment"
              matTooltip="Add comment"
            >
              <mat-icon>add_comment</mat-icon>
            </button>
          }
          <!-- Existing edit/drag logic -->
          @if (editMode && isSection(node)) {
            <div cdkDrag [cdkDragDisabled]="!editMode" [cdkDragData]="node" class="child-section-drag-container">
              @if (nodeFactory.getComponentForNodeType(node.type); as component) {
                @if (node?.comments?.length) {
                  <ng-container
                    *ngComponentOutlet="component; injector: nodeFactory.createInjector(node, parentDocument, injector)"
                  ></ng-container>
                } @else {
                  <ng-container
                    *ngComponentOutlet="component; injector: nodeFactory.createInjector(node, parentDocument, injector)"
                  ></ng-container>
                }
              }
              @if (!nodeFactory.getComponentForNodeType(node.type)) {
                <div>Unknown node type</div>
              }
            </div>
          } @else {
            @if (editMode) {
              <div class="inline-add-before">
                <button
                  mat-mini-fab
                  color="primary"
                  class="add-btn"
                  (click)="addButtonClicked(i)"
                  matTooltip="Add before"
                  aria-label="Add node before"
                >
                  <mat-icon>add</mat-icon>
                </button>
                <span class="add-label">Add</span>
                @if (showingAddAt === i) {
                  <span class="add-controls">
                    <select [(ngModel)]="newNodeTypeSelection[i]">
                      @for (type of nodeTypes; track type) {
                        <option [ngValue]="type">{{ nodeTypeLabel[type] }}</option>
                      }
                    </select>
                    <button
                      mat-mini-fab
                      color="primary"
                      class="confirm-btn"
                      (click)="confirmAdd(i)"
                      matTooltip="Confirm add"
                      aria-label="Confirm add"
                    >
                      <mat-icon>check</mat-icon>
                    </button>
                    <button mat-mini-fab class="cancel-btn" (click)="cancelAdd()" matTooltip="Cancel" aria-label="Cancel add">
                      <mat-icon>close</mat-icon>
                    </button>
                  </span>
                }
              </div>
            }
            @if (nodeFactory.getComponentForNodeType(node.type); as component) {
              <ng-container
                *ngComponentOutlet="component; injector: nodeFactory.createInjector(node, parentDocument, injector)"
              ></ng-container>
              @if (node?.comments?.length > 0) {
                @for (comment of node.comments; track comment) {
                  <app-comment-rail [nodeId]="node?.id" [reviewerMode]="false" [comment]="comment"> </app-comment-rail>
                }
              }
            }
            @if (!nodeFactory.getComponentForNodeType(node.type)) {
              <div>Unknown node type</div>
            }
          }
        </div>
      }
    </div>

    @if (editMode) {
      <!-- Add button at the end of the section -->
      <div class="section-add-bottom">
        <button
          mat-mini-fab
          color="primary"
          class="add-btn"
          (click)="addButtonClicked('end')"
          matTooltip="Add node at end"
          aria-label="Add node at end"
        >
          <mat-icon>add</mat-icon>
        </button>
        <span class="add-label">Add</span>
        @if (showingAddAt === 'end') {
          <span class="add-controls">
            <select [(ngModel)]="newNodeTypeSelection['end']">
              @for (type of nodeTypes; track type) {
                <option [ngValue]="type">{{ nodeTypeLabel[type] }}</option>
              }
            </select>
            <button
              mat-mini-fab
              color="primary"
              class="confirm-btn"
              (click)="confirmAdd('end')"
              matTooltip="Confirm add"
              aria-label="Confirm add"
            >
              <mat-icon>check</mat-icon>
            </button>
            <button mat-mini-fab class="cancel-btn" (click)="cancelAdd()" matTooltip="Cancel" aria-label="Cancel add">
              <mat-icon>close</mat-icon>
            </button>
          </span>
        }
      </div>

      <!-- Keep only the add-sibling action here; delete moved to top-right -->
      <div class="section-actions">
        <div class="section-add-sibling">
          <button
            mat-mini-fab
            color="accent"
            class="add-btn"
            (click)="addSiblingAfter()"
            matTooltip="Add section below"
            aria-label="Add section below"
          >
            <mat-icon>add</mat-icon>
          </button>
          <span class="add-label">Add section below</span>
        </div>
      </div>
    }
  </mat-card-content>
</mat-card>
